<template>
  <div class="lead-conversations">
    <BasePaneTray
      context="leadConversationsContext"
      :isOpen="isOpen"
      heading="Conversas"
    >
      <template #headerPlus>
        <div class="lead-conversations__heading">
          {{ heading }}
        </div>
      </template>

      <template #body>
        <div class="lead-conversations__body">
          <template v-if="dataset.length">
            <ul class="lead-conversations__list">
              <li
                class="lead-conversations__list__item"
                v-for="(item, index) in dataset"
                :key="index"
              >
                <div class="lead-conversations__list__item__content">
                  <div class="lead-conversations__list__item__content__name">
                    {{ item.name | startCase }}
                  </div>

                  <div class="lead-conversations__list__item__content__details">
                    {{ getLeadDetailsMessage(item) }}
                  </div>
                </div>

                <button
                  class="lead-conversations__list__item__button-history"
                  v-tooltip="{
                    theme: 'tooltip-top-left',
                    content: 'Veja o histórico<br>da conversa'
                  }"
                  @click.prevent="openLeadHistory(item)"
                >
                  <SvgIconChat/>
                </button>
              </li>
            </ul>

            <button
              class="lead-conversations__button-pagination"
              :class="{ 'lead-conversations__button-pagination--off': isLoadingPagination }"
              :disabled="isDisabledPagination"
              @click.prevent="fetchData(true)"
            >
              <BaseLoaderDefault v-if="isLoadingPagination"/>

              <span v-else>
                Ver mais
              </span>
            </button>
          </template>

          <BaseEmptyState v-else>
            <template v-if="dataset.error">
              <SvgIconRobot/>

              <h3>
                Ops! Algo deu errado...
              </h3>

              <p>
                Por favor tente novamente.
              </p>
            </template>

            <template v-else>
              <SvgIconPersonSearch/>

              <h3>
                Nenhum conversa para ser exibida.
              </h3>
            </template>
          </BaseEmptyState>

          <div
            v-if="isLoading"
            class="lead-conversations__loader"
          >
            <BaseLoaderDefault/>
          </div>
        </div>
      </template>
    </BasePaneTray>

    <CommonLeadHistory
      :isOpen="isOpenLeadHistory"
      :heading="leadHistoryHeading"
      :subheading="leadHistorySubheading"
      :leadId="leadSelected.id"
      :historyId="leadSelected.historyId"
    />
  </div>
</template>

<script>
import { dateFilter, startCaseFilter } from '@/utils/filters'
import BasePaneTray from '@/components/base/BasePaneTray'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import BaseLoaderDefault from '@/components/base/BaseLoaderDefault'
import CommonLeadHistory from '@/components/common/CommonLeadHistory'
import { mapState } from 'vuex'
import axios from 'axios'

export default {
  name: 'CommonLeadConversations',

  components: {
    BasePaneTray,
    BaseEmptyState,
    BaseLoaderDefault,
    CommonLeadHistory
  },

  props: {
    isOpen: Boolean,
    heading: String,
    urlParams: String
  },

  filters: {
    date: dateFilter,
    startCase: startCaseFilter
  },

  data () {
    return {
      dataset: [],
      isLoading: false,
      isLoadingPagination: false,
      isDisabledPagination: false,
      isOpenLeadHistory: false,
      leadSelected: {
        id: null,
        historyId: null,
        name: null
      }
    }
  },

  computed: {
    ...mapState('base', ['mainApi']),

    leadHistoryHeading () {
      const { name } = this.leadSelected

      return name
        ? startCaseFilter(name)
        : ''
    },

    leadHistorySubheading () {
      const { id } = this.leadSelected

      return id
        ? `ID #${id}`
        : ''
    }
  },

  watch: {
    urlParams () {
      this.resetDataset()
      this.fetchData()
    }
  },

  mounted () {
    this.$root.$on('pane-tray-close', this.closeLeadHistory)
  },

  methods: {
    resetDataset () {
      this.dataset = []
      this.isDisabledPagination = false
    },

    getUrlParams () {
      const lastItem = this.dataset[this.dataset.length - 1]
      let { urlParams } = this

      if (lastItem) {
        urlParams += `&paginationDateTime=${lastItem.clh_created_at || lastItem.created_at}`
      }

      return urlParams
    },

    async fetchData (paginationMode) {
      if (!this.isOpen) {
        return
      }

      if (paginationMode) {
        this.isLoadingPagination = true
      } else {
        this.isLoading = true
      }

      try {
        const params = this.getUrlParams()
        const url = `${this.mainApi}lead/message/chat-list${params}`
        const response = await axios.get(url)

        if (response.data.length < 10) {
          this.isDisabledPagination = true
        }

        this.dataset = [...this.dataset, ...response.data]
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        if (paginationMode) {
          this.isLoadingPagination = false
        } else {
          this.isLoading = false
        }
      }
    },

    selectLead (lead) {
      const {
        id_lead: id,
        id_lead_history: historyId,
        name
      } = lead

      this.leadSelected = {
        id,
        historyId,
        name
      }
    },

    openLeadHistory (lead) {
      this.selectLead(lead)

      this.isOpenLeadHistory = true
    },

    closeLeadHistory () {
      this.isOpenLeadHistory = false
    },

    getLeadDetailsMessage (lead) {
      const { id_lead, created_at, clh_created_at } = lead
      const date = clh_created_at || created_at
      let message = `#${id_lead} - `

      if (clh_created_at) {
        message += `Finalizado em `
      }

      message += dateFilter(date, 'date')

      return message
    }
  }
}
</script>

<style lang="scss" scoped>
.lead-conversations {
  &__heading {
    padding: 10px 20px;
    border-radius: 2rem;
    background: nth($colors-custom, 4);
    font: {
      weight: 500;
      size: 10px;
    }
    line-height: 140%;
    text-align: center;
  }

  &__body {
    position: relative;
    height: 100%;
    overflow: auto;
    padding-right: 15px;
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    display: flex;
    width: 100%;
    height: 100%;
    justify-content: center;
    align-items: center;
    background: #fff;
  }

  &__list {
    &__item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 15px;
      padding: 15px 0;

      &:not(:first-child) {
        border-top: 1px solid nth($colors-custom, 4);
      }

      &__content {
        &__name {
          font: {
            weight: 600;
            size: 14px;
          }
          line-height: 120%;
        }

        &__details {
          margin-top: 5px;
          font: {
            weight: 400;
            size: 11px;
          }
          line-height: 100%;
        }
      }

      &__button-history {
        flex-shrink: 0;
        display: flex;
        width: 32px;
        height: 32px;
        justify-content: center;
        align-items: center;
        border: 1px solid nth($colors-custom, 1);
        border-radius: 100%;
        transition: all 0.1s;
        background: none;

        &:hover {
          border-color: nth($colors-custom, 2);
          color: nth($colors-custom, 2);

          svg {
            fill: nth($colors-custom, 2);
          }
        }

        svg {
          width: 14px;
          transition: all 0.1s;
          fill: nth($colors-custom, 1);
        }
      }
    }
  }

  &__button-pagination {
    display: flex;
    width: 80px;
    height: 30px;
    justify-content: center;
    align-items: center;
    margin: 30px auto 0;
    border: 1px solid nth($colors-custom, 1);
    border-radius: 2em;
    transition: all 0.1s;
    background: none;
    color: nth($colors-custom, 1);
    font: {
      weight: 400;
      size: 12px;
    }

    &:disabled {
      opacity: 0.3;
    }

    &:hover {
      border-color: nth($colors-custom, 2);
      background: nth($colors-custom, 2);
    }

    &--off {
      border-color: nth($colors-custom, 2);
      pointer-events: none;
    }
  }
}
</style>
